import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import { WidgetComponent } from '@juvare/widget-ui/WidgetComponent';
import SquareIcon from '@mui/icons-material/Square';
import { Box, Typography, useTheme } from '@mui/material';
import React from 'react';
import { HtmlOutput } from '../HtmlOutput';
import { fieldsetLegendReset } from '../utils/stylesReset';
import { buildGeneralTab, buildHiddenTab, buildStyleTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { DragTargetPlaceholder } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getChildWidgetStyle, getLabelAndValuePairsFromChildProps, getVisibleError, handleWidgetUpdate, joinLabels, makeNewConfigUnique, } from '../WidgetForm/Utils';
import { TemplateOutput } from '../WidgetFormContent/Components';
import { DropTargetSpacerHorizontal } from '../WidgetFormPanel/Components';
import { WidgetHandler } from '../WidgetHandler';
import { type } from './types';
const WidgetFormLabel = (props) => {
    var _a, _b, _c, _d, _e;
    let customContent = null;
    const theme = useTheme();
    if (props.config.render) {
        customContent = React.createElement(Typography, { component: 'div' }, props.config.render(new WidgetHandler(props)));
    }
    else if (props.config.template) {
        customContent = (React.createElement(Typography, { component: 'div' },
            React.createElement(TemplateOutput, { template: props.config.template, props: props })));
    }
    else if (props.config.html) {
        customContent = (React.createElement(Typography, { component: 'div' },
            React.createElement(HtmlOutput, { html: props.config.html, customTags: (_a = props.rootParams.globalWidgetConfig) === null || _a === void 0 ? void 0 : _a.htmlOutputCustomTags, data: (_b = props.rootParams.globalWidgetConfig) === null || _b === void 0 ? void 0 : _b.htmlOutputData, customIcons: (_c = props.rootParams.globalWidgetConfig) === null || _c === void 0 ? void 0 : _c.htmlOutputIcons })));
    }
    const childProps = props.childProps && props.childProps.length > 0 ? props.childProps[0] : undefined;
    const horizontal = ((_d = props.config.style) === null || _d === void 0 ? void 0 : _d.layout) === 'horizontal' && (childProps || props.editMode);
    const componentWidth = (_e = props.config.style) === null || _e === void 0 ? void 0 : _e.componentWidth;
    const fieldsetStyling = horizontal
        ? Object.assign(Object.assign({}, fieldsetLegendReset), { display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', [theme.breakpoints.down('sm')]: {
                display: 'block',
            } }) : fieldsetLegendReset;
    return (React.createElement(Box, { component: 'fieldset', sx: fieldsetStyling },
        React.createElement(Box, { component: 'legend', sx: Object.assign(Object.assign({}, fieldsetLegendReset), (horizontal ? { float: 'left', '::after': { content: '""', clear: 'both', display: 'table' } } : {})) },
            props.config.label && (React.createElement(React.Fragment, null,
                React.createElement(Typography, { variant: 'body2', "data-test": 'label' }, props.config.label),
                props.config.description && (React.createElement(Typography, { variant: 'body2', color: 'textSecondary', sx: { pt: 1, whiteSpace: 'pre-wrap' }, "data-test": 'description' }, props.config.description)))),
            !props.config.label && props.config.description && (React.createElement(Typography, { variant: 'body2', "data-test": 'description' }, props.config.description)),
            customContent && (React.createElement(Typography, { component: 'div', sx: { pt: props.config.label || props.config.description ? 1 : 0 }, "data-test": 'content' }, customContent))),
        (childProps || props.editMode) && (React.createElement(Box, { sx: horizontal
                ? (theme) => ({
                    flexGrow: 1,
                    [theme.breakpoints.down('sm')]: {
                        pt: 1,
                    },
                })
                : { pt: 1 } },
            childProps && (React.createElement(Box, { sx: horizontal
                    ? (theme) => ({
                        width: componentWidth,
                        marginLeft: 'auto',
                        [theme.breakpoints.down('sm')]: { width: 'auto', marginLeft: 0 },
                    })
                    : (theme) => ({
                        width: componentWidth,
                        [theme.breakpoints.down('sm')]: { width: 'auto' },
                    }), style: getChildWidgetStyle(childProps), "data-editor-path": 'component', "data-widget": childProps.dataPath, "data-visible-error": getVisibleError(childProps) ? 'true' : undefined },
                React.createElement(WidgetComponent, Object.assign({}, childProps, { renderData: props.renderData })))),
            !childProps && props.editMode && (React.createElement(Box, { sx: horizontal
                    ? (theme) => ({
                        width: componentWidth,
                        marginLeft: 'auto',
                        p: 1,
                        [theme.breakpoints.down('sm')]: {
                            width: 'auto',
                            marginLeft: 0,
                            pt: 0,
                            pb: 1,
                            pl: 2,
                            pr: 2,
                        },
                    })
                    : (theme) => ({
                        width: componentWidth,
                        pl: 2,
                        pr: 2,
                        pb: 1,
                        [theme.breakpoints.down('sm')]: {
                            width: 'auto',
                        },
                    }) },
                React.createElement(DropTargetSpacerHorizontal, { spacing: 16, first: true, last: true, editorPath: 'component', placeholderElement: React.createElement(DragTargetPlaceholder, null, "Drop here") })))))));
};
const WidgetFormLabelEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, {
            title: 'Label',
        }, buildGeneralTab(props, {
            labelOptional: true,
            description: true,
            children: [
                {
                    type: 'form.radio',
                    key: 'contentType',
                    label: 'Custom Content',
                    values: [
                        { value: 'none', label: 'None' },
                        { value: 'html', label: 'HTML' },
                        { value: 'template', label: 'Template' },
                    ],
                },
                {
                    type: 'form.html',
                    key: 'html',
                    label: 'HTML',
                    rows: 10,
                    hidden: {
                        when: '^contentType',
                        dataNotEq: 'html',
                    },
                },
                {
                    type: 'form.template',
                    key: 'template',
                    label: 'Template',
                    hidden: {
                        when: '^contentType',
                        dataNotEq: 'template',
                    },
                    props: props,
                },
            ],
            onLoad: (data) => {
                if (data.template != null) {
                    data.contentType = 'template';
                }
                else if (data.html != null) {
                    data.contentType = 'html';
                }
                else {
                    data.contentType = 'none';
                }
                return data;
            },
            onSubmit: (data) => {
                if (data.contentType === 'template') {
                    delete data.html;
                }
                else if (data.contentType === 'html') {
                    delete data.template;
                }
                else {
                    delete data.template;
                    delete data.html;
                }
                delete data.contentType;
                return data;
            },
        }), buildHiddenTab(props), buildStyleTab(props, {
            children: [
                {
                    type: 'form.radio',
                    key: 'style.layout',
                    label: 'Layout',
                    values: [
                        { value: 'vertical', label: 'Vertical' },
                        { value: 'horizontal', label: 'Horizontal' },
                    ],
                },
                {
                    type: 'form.number',
                    key: 'style.componentWidth',
                    label: 'Component Width',
                    style: {
                        width: 200,
                    },
                },
            ],
            onLoad: (data) => {
                if (!data.style) {
                    data.style = {};
                }
                if (!data.style.layout) {
                    data.style.layout = 'vertical';
                }
                return data;
            },
            onSubmit: (data) => {
                var _a;
                if (((_a = data.style) === null || _a === void 0 ? void 0 : _a.layout) === 'vertical') {
                    delete data.style.layout;
                    if (Object.keys(data.style).length === 0) {
                        delete data.style;
                    }
                }
                return data;
            },
        })) }),
        React.createElement(WidgetFormLabel, Object.assign({}, props))));
};
export const widgetFormLabel = {
    type,
    update: (props) => {
        if (props.config.template || props.config.render) {
            // need to always rerender because template or render depends on values from other components
            props.update();
        }
        return handleWidgetUpdate(props);
    },
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        return getLabelAndValuePairsFromChildProps(props, joinLabels(labelPrefix, props.config.label));
    },
    childConfigs: (config) => (config.component ? [config.component] : []),
    component: WidgetFormLabel,
    icon: React.createElement(SquareIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormLabelEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, label: 'Label' }),
};
